/**
 * CommandBar — the ⌘K command palette opened from the top bar's search pill
 * (or the Cmd/Ctrl+K shortcut, wired in AppShell). Portal to <body> so it
 * escapes the top bar's stacking context. Lists the six global destinations
 * (see globalNav.tsx — one shared list with the icon rail) plus the current
 * account's projects (all projects when the global account filter is "All
 * accounts"), filtered as you type. ↑/↓ move the highlighted row, Enter
 * navigates + closes, Escape closes. No external command-palette library —
 * this is a plain filtered list + keyboard handler, matching the app's
 * existing portal-menu idiom (see ProjectsMenu in AppShell.tsx).
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import { useProjects } from '../api/project';
import { useSelectedAccount } from '../store/selectedAccountStore';
import { GLOBAL_NAV_ITEMS } from './globalNav';

function IconSearchGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function IconProjectGlyph() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    </svg>
  );
}

interface CommandItem {
  id: string;
  label: string;
  sublabel: string;
  icon: ReactNode;
  path: string;
}

export interface CommandBarProps {
  open: boolean;
  onClose: () => void;
}

export function CommandBar({ open, onClose }: CommandBarProps) {
  const navigate = useNavigate();
  const { data: projects = [], isError: projectsError } = useProjects();
  const accountId = useSelectedAccount((s) => s.accountId);
  const [query, setQuery] = useState('');
  const [highlighted, setHighlighted] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Reset to a clean slate every time the palette opens, and focus the input
  // once it's actually in the DOM (portal mount happens synchronously with
  // this render, but the ref isn't attached until after paint).
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setHighlighted(0);
    const id = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [open]);

  const scopedProjects = accountId ? projects.filter((p) => p.accountId === accountId) : projects;

  const items: CommandItem[] = useMemo(() => {
    const destinations: CommandItem[] = GLOBAL_NAV_ITEMS.map((item) => ({
      id: `dest-${item.id}`,
      label: item.label,
      sublabel: 'Destination',
      icon: <item.Icon />,
      path: item.path,
    }));
    const projectItems: CommandItem[] = scopedProjects.map((p) => ({
      id: `project-${p.id}`,
      label: p.name,
      sublabel: 'Project',
      icon: <IconProjectGlyph />,
      path: `/project/${p.id}`,
    }));
    return [...destinations, ...projectItems];
    // eslint-disable-next-line react-hooks/exhaustive-deps -- scopedProjects is a fresh array each render; keying on its content (ids) would over-complicate this for a small list.
  }, [scopedProjects.length, accountId, projects]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? items.filter((i) => i.label.toLowerCase().includes(q)) : items;
  }, [items, query]);

  // Filtering can shrink the list out from under a highlighted index that no
  // longer exists — clamp rather than reset to 0 so ArrowUp/Down feels stable
  // while typing.
  useEffect(() => {
    setHighlighted((h) => Math.min(h, Math.max(filtered.length - 1, 0)));
  }, [filtered.length]);

  function select(item: CommandItem) {
    onClose();
    navigate(item.path);
  }

  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlighted((h) => Math.min(h + 1, filtered.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlighted((h) => Math.max(h - 1, 0));
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        const item = filtered[highlighted];
        if (item) select(item);
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- select/navigate/onClose are stable enough for this handler's purpose; re-binding on every filtered/highlighted change is the point.
  }, [open, filtered, highlighted]);

  if (!open) return null;

  return createPortal(
    // z-band contract (matches FormModal's exactly): drawers occupy 199
    // (scrim) / 200 (panel); centered modals/palettes occupy 299 (scrim) / 300
    // (panel) — one band above drawers so this always wins visually even if
    // it somehow opens over one (defense-in-depth; the primary guard is the
    // AppShell ⌘K handler's open-dialog suppression). This one div is both
    // the scrim (background+inset:0) AND the centering container — its
    // zIndex is the "scrim" half of the pair; the dialog panel below is a
    // flex child, and flex items respect z-index without needing `position`.
    <div
      role="presentation"
      onMouseDown={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--overlay-scrim)',
        zIndex: 299,
        display: 'flex',
        justifyContent: 'center',
        paddingTop: '12vh',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onMouseDown={(e) => e.stopPropagation()}
        className="pop-in"
        style={{
          width: 560,
          maxWidth: 'calc(100vw - 48px)',
          maxHeight: '60vh',
          zIndex: 300,
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-panel)',
          boxShadow: 'var(--shadow-2)',
          overflow: 'hidden',
          ['--pop-origin' as string]: 'top center',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '13px 16px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          <span style={{ display: 'flex', color: 'var(--text-subtle)', flexShrink: 0 }}>
            <IconSearchGlyph />
          </span>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search destinations and projects…"
            aria-label="Search destinations and projects"
            autoComplete="off"
            spellCheck={false}
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-base)',
              color: 'var(--text)',
            }}
          />
          <kbd
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--text-2xs)',
              fontWeight: 600,
              padding: '2px 6px',
              borderRadius: 'var(--radius-xs)',
              background: 'var(--kbd-bg)',
              border: '1px solid var(--kbd-border)',
              color: 'var(--kbd-fg)',
              flexShrink: 0,
            }}
          >
            Esc
          </kbd>
        </div>

        <div ref={listRef} role="listbox" aria-label="Results" style={{ overflowY: 'auto', padding: 6 }}>
          {/* Silent-failure audit finding 13 — without this, a failed
              useProjects fetch just means project results never appear, with
              nothing telling the user why their project search comes up
              empty. Destinations (not network-dependent) render normally
              alongside it. */}
          {projectsError && (
            <div
              role="alert"
              data-testid="command-bar-projects-error"
              style={{
                padding: '10px 12px',
                marginBottom: 4,
                background: 'var(--error-bg)',
                border: '1px solid var(--error-border)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--error)',
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--text-xs)',
                fontWeight: 600,
              }}
            >
              Couldn&apos;t load projects
            </div>
          )}
          {filtered.length === 0 && (
            <div style={{ padding: '22px 12px', textAlign: 'center', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)' }}>
              No matches
            </div>
          )}
          {filtered.map((item, i) => {
            const isHighlighted = i === highlighted;
            return (
              <button
                key={item.id}
                type="button"
                role="option"
                aria-selected={isHighlighted}
                onMouseEnter={() => setHighlighted(i)}
                onClick={() => select(item)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  width: '100%',
                  padding: '9px 10px',
                  marginBottom: 2,
                  border: 'none',
                  borderRadius: 'var(--radius-sm)',
                  textAlign: 'left',
                  cursor: 'pointer',
                  background: isHighlighted ? 'var(--brand-soft)' : 'transparent',
                  color: isHighlighted ? 'var(--brand)' : 'var(--text)',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: isHighlighted ? 600 : 400,
                }}
              >
                <span style={{ display: 'flex', flexShrink: 0, color: isHighlighted ? 'var(--brand)' : 'var(--text-subtle)' }}>{item.icon}</span>
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</span>
                {/* §1.5 — sentence case; no all-caps labels. QA R2: --text-low
                    (not --text-subtle) — measured 3.59:1 dark / 3.76:1 light
                    against the palette card (below 4.5:1); --text-low clears
                    4.78:1 dark / 5.70:1 light. */}
                <span
                  style={{
                    flexShrink: 0,
                    fontFamily: 'var(--font-mono)',
                    fontSize: 'var(--text-2xs)',
                    color: 'var(--text-low)',
                  }}
                >
                  {item.sublabel}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>,
    document.body,
  );
}
